import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, CircularProgress, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { activityLogService } from '../services/api';
import api from '../services/api';

const ACTION_COLORS = {
  login: 'default',
  create: 'success', production_create: 'success', order_create: 'success',
  purchase_create: 'success', lead_create: 'success', project_create: 'success',
  mixing_create: 'success', maintenance_create: 'warning',
  update: 'info', production_update: 'info', order_update: 'info', project_update: 'info',
  delete: 'error',
  stock_arrival: 'success', stock_consumption: 'warning', stock_transfer: 'info',
  stock_block: 'error',
  production_start: 'primary', production_complete: 'success', production_cancel: 'error',
  order_ship: 'success', purchase_receive: 'success', purchase_approve: 'info',
  maintenance_complete: 'success',
  qc_fail: 'error', qc_pass: 'success',
  mixing_complete: 'success', packaging_complete: 'success',
  lead_won: 'success', lead_lost: 'error',
};

const PAGE_SIZE = 50;

export default function ActivityLogPage({ currentUser }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actionChoices, setActionChoices] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [filters, setFilters] = useState({
    action_type: '', entity_type: '', staff: '', date_from: '', date_to: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.action_type) params.action_type = filters.action_type;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.staff) params.staff = filters.staff;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await activityLogService.getAll(params);
      const data = res.data;
      if (Array.isArray(data)) {
        setLogs(data);
        setTotal(data.length);
      } else {
        setLogs(data.results || []);
        setTotal(data.count || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    activityLogService.getActionChoices().then(r => setActionChoices(r.data));
    api.get('/staff/').then(r => setStaffList(Array.isArray(r.data) ? r.data : r.data.results || []));
  }, []);

  const entityTypes = [
    'ProductionOrder','CustomerOrder','PurchaseOrder','StockArrival',
    'MaintenanceTicket','MixingBatch','Lead','Project','PressForm',
    'Product','RawMaterial','Customer','Supplier','Staff',
  ];

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Журнал подій</Typography>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Тип події</InputLabel>
          <Select value={filters.action_type} label="Тип події" onChange={e => setFilter('action_type', e.target.value)}>
            <MenuItem value="">Всі</MenuItem>
            {actionChoices.map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Сутність</InputLabel>
          <Select value={filters.entity_type} label="Сутність" onChange={e => setFilter('entity_type', e.target.value)}>
            <MenuItem value="">Всі</MenuItem>
            {entityTypes.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Користувач</InputLabel>
          <Select value={filters.staff} label="Користувач" onChange={e => setFilter('staff', e.target.value)}>
            <MenuItem value="">Всі</MenuItem>
            {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.full_name || s.username}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField size="small" type="date" label="Від" value={filters.date_from}
          onChange={e => setFilter('date_from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="До" value={filters.date_to}
          onChange={e => setFilter('date_to', e.target.value)} InputLabelProps={{ shrink: true }} />

        <Tooltip title="Оновити">
          <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {total} записів
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 150 }}>Час</TableCell>
                <TableCell sx={{ width: 140 }}>Користувач</TableCell>
                <TableCell sx={{ width: 200 }}>Подія</TableCell>
                <TableCell sx={{ width: 140 }}>Сутність</TableCell>
                <TableCell>Деталі</TableCell>
                <TableCell sx={{ width: 120 }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Подій немає</TableCell></TableRow>
              )}
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {new Date(log.created_at).toLocaleString('uk-UA')}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{log.staff_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action_label}
                      size="small"
                      color={ACTION_COLORS[log.action_type] || 'default'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {log.entity_type && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{log.entity_type}</Typography>
                        {log.entity_id && <Typography variant="caption" color="text.secondary"> #{log.entity_id}</Typography>}
                        {log.entity_name && <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{log.entity_name}</Typography>}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.description}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{log.ip}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Pagination count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} />
        </Box>
      )}
    </Box>
  );
}
